/**
 * <product-carousel>
 * ---------------------------------------------------------------------------
 * Flechas para una vitrina que ya se desplaza sola.
 *
 * El carrusel es scroll-snap del navegador, no posiciones animadas a mano: el
 * gesto táctil, la rueda del ratón y el arrastre con trackpad salen gratis, y
 * sin JavaScript la lista se sigue desplazando igual. Lo único que añade este
 * archivo son las dos flechas, que en escritorio no tienen sustituto: sin ratón
 * de rueda horizontal no hay forma cómoda de avanzar.
 *
 * Por eso las flechas se pintan ocultas y las enciende este archivo: si no se
 * ejecuta, nadie ve dos botones muertos.
 *
 * Envuelto en IIFE: el editor de temas recarga la sección y dos <script> con la
 * misma URL se ejecutan dos veces. Sin el envoltorio, el `class` del ámbito
 * global choca y el archivo entero deja de funcionar.
 */

(() => {
  class ProductCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-carousel-track]');
      this.prev = this.querySelector('[data-carousel-prev]');
      this.next = this.querySelector('[data-carousel-next]');
      if (!this.track || !this.prev || !this.next) return;

      this.prev.addEventListener('click', () => this.#scrollByPage(-1));
      this.next.addEventListener('click', () => this.#scrollByPage(1));
      this.track.addEventListener('scroll', this.#onScroll, { passive: true });

      // Al cambiar el ancho cambia cuántas fichas caben, y con ello si hacen
      // falta flechas. `ResizeObserver` y no `resize` de window: la sección
      // también cambia de ancho al abrirse el panel del editor de temas.
      this.observer = new ResizeObserver(() => this.#update());
      this.observer.observe(this.track);

      this.#update();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
    }

    /**
     * Avanza una pantalla, no una ficha. Pulsar cuatro veces para pasar de
     * página cansa, y el snap deja igualmente cada ficha alineada al borde.
     */
    #scrollByPage(direction) {
      this.track.scrollBy({
        left: direction * this.track.clientWidth,
        behavior: this.#prefersReducedMotion ? 'auto' : 'smooth',
      });
    }

    #onScroll = () => {
      // El scroll dispara decenas de eventos por gesto; basta con mirar el
      // estado una vez por fotograma.
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.#update());
    };

    #update() {
      const { scrollWidth, clientWidth } = this.track;
      // `scrollLeft` es negativo en RTL: el valor absoluto sirve en los dos
      // sentidos de lectura.
      const position = Math.abs(this.track.scrollLeft);
      // Un píxel de margen: con `zoom` del navegador o anchos fraccionarios,
      // el final del recorrido no cae nunca en un entero exacto y la flecha se
      // quedaría activa sin nada que mostrar.
      const overflows = scrollWidth - clientWidth > 1;

      this.classList.toggle('has-controls', overflows);
      this.prev.disabled = !overflows || position <= 1;
      this.next.disabled = !overflows || position >= scrollWidth - clientWidth - 1;
    }

    get #prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
  }

  if (!customElements.get('product-carousel')) {
    customElements.define('product-carousel', ProductCarousel);
  }
})();
